import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, ChevronLeft, ChevronRight, Flag, Clock } from "lucide-react";
import api from "@/lib/api";

const DOW = ["L", "M", "X", "J", "V", "S", "D"];
const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const PRIORITY_COLOR = {
  urgente: "#DC2626",
  alta: "#E08A1F",
  normal: "var(--legatum-accent)",
  baja: "#64748B",
};

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/**
 * Mini calendario embebido en el dashboard.
 *
 * - Mes navegable (prev / hoy / next)
 * - Días con tareas activas se marcan con punto del color de la prioridad máxima
 * - Próximas 5 tareas ordenadas por fecha
 */
export default function DashboardCalendarWidget() {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [tareas, setTareas] = useState([]);

  useEffect(() => {
    // Cargar tareas del mes en curso + 60 días hacia delante (para "próximas")
    const desde = ymdLocal(startOfMonth(cursor));
    const hasta = ymdLocal(addDays(endOfMonth(cursor), 60));
    api.get(`/tareas?desde=${desde}&hasta=${hasta}&mine=true`)
      .then((r) => setTareas(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setTareas([]));
  }, [cursor]);

  // Mapa día (YYYY-MM-DD) → tareas
  const byDay = useMemo(() => {
    const map = new Map();
    for (const t of tareas) {
      const ini = t.fecha_inicio || t.fecha_fin;
      const fin = t.fecha_fin || t.fecha_inicio;
      if (!ini && !fin) continue;
      // Marcar todos los días entre ini y fin (cap 31 para no explotar)
      const d0 = ini ? new Date(`${ini}T00:00:00`) : new Date(`${fin}T00:00:00`);
      const d1 = fin ? new Date(`${fin}T00:00:00`) : d0;
      let cur = new Date(d0);
      let safety = 0;
      while (cur <= d1 && safety < 31) {
        const k = ymdLocal(cur);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(t);
        cur = addDays(cur, 1);
        safety += 1;
      }
    }
    return map;
  }, [tareas]);

  // Construir grilla 6x7 del mes
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    // Lunes=0 ... Domingo=6
    const firstDow = (first.getDay() + 6) % 7;
    const cells = [];
    // Días en blanco al inicio
    for (let i = 0; i < firstDow; i++) cells.push(null);
    // Días del mes
    for (let d = 1; d <= last.getDate(); d++) {
      cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    // Rellenar hasta múltiplo de 7
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const upcoming = useMemo(() => {
    const todayY = ymdLocal(today);
    return tareas
      .filter((t) => (t.fecha_fin || t.fecha_inicio) && (t.fecha_fin || t.fecha_inicio) >= todayY)
      .filter((t) => t.estado !== "completada" && t.estado !== "cancelada")
      .sort((a, b) => (a.fecha_fin || a.fecha_inicio || "").localeCompare(b.fecha_fin || b.fecha_inicio || ""))
      .slice(0, 5);
  }, [tareas, today]);

  const maxPriority = (list) => {
    const order = { urgente: 4, alta: 3, normal: 2, baja: 1 };
    return list.reduce((acc, t) => (order[t.prioridad] || 0) > (order[acc] || 0) ? t.prioridad : acc, "baja");
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-8 md:mb-10"
      data-testid="dashboard-calendar-widget"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#0E9488]/15 text-[#0E9488] border border-[#0E9488]/30">
            <CalendarDays className="h-3.5 w-3.5" />
          </div>
          <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-[var(--legatum-text-muted)]">
            Mi calendario
          </h2>
        </div>
        <Link
          to="/calendario"
          className="inline-flex items-center gap-1 text-xs font-bold text-[var(--legatum-accent)] hover:underline"
          data-testid="dashboard-calendar-link"
        >
          Ver calendario completo <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div
        className="grid grid-cols-1 lg:grid-cols-5 gap-4"
      >
        {/* Calendario mensual */}
        <div
          className="lg:col-span-3 rounded-2xl p-4"
          style={{ background: "var(--legatum-surface)", border: "1px solid var(--legatum-border)" }}
        >
          {/* Navegación de mes */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
              data-testid="calendar-widget-prev"
              className="h-7 w-7 rounded-md flex items-center justify-center hover:bg-[var(--legatum-elevated)] text-[var(--legatum-text-secondary)]"
              aria-label="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-sm font-bold text-[var(--legatum-text)] capitalize">
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCursor(startOfMonth(new Date()))}
                data-testid="calendar-widget-today"
                className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-secondary)] hover:bg-[var(--legatum-elevated)]"
              >
                Hoy
              </button>
              <button
                type="button"
                onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
                data-testid="calendar-widget-next"
                className="h-7 w-7 rounded-md flex items-center justify-center hover:bg-[var(--legatum-elevated)] text-[var(--legatum-text-secondary)]"
                aria-label="Mes siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Cabecera días */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW.map((d) => (
              <div key={d} className="text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)] text-center py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Grilla días */}
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              if (!d) return <div key={`b-${i}`} className="h-10" />;
              const k = ymdLocal(d);
              const isToday = k === ymdLocal(today);
              const list = byDay.get(k) || [];
              const has = list.length > 0;
              const prio = has ? maxPriority(list) : null;
              const dotColor = prio ? PRIORITY_COLOR[prio] || PRIORITY_COLOR.normal : null;
              return (
                <Link
                  key={k}
                  to={`/calendario?fecha=${k}`}
                  data-testid={`calendar-widget-day-${k}`}
                  className={`relative h-10 rounded-md flex flex-col items-center justify-center transition-colors text-xs ${
                    isToday
                      ? "bg-[var(--legatum-accent)]/15 border border-[var(--legatum-accent)]/50 text-[var(--legatum-accent)] font-extrabold"
                      : "text-[var(--legatum-text-secondary)] hover:bg-[var(--legatum-elevated)]"
                  }`}
                >
                  <span className="tabular-nums leading-none">{d.getDate()}</span>
                  {has && (
                    <span
                      className="absolute bottom-1 h-1.5 w-1.5 rounded-full"
                      style={{ background: dotColor }}
                    />
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Próximas tareas */}
        <div
          className="lg:col-span-2 rounded-2xl p-4"
          style={{ background: "var(--legatum-surface)", border: "1px solid var(--legatum-border)" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-3.5 w-3.5 text-[var(--legatum-accent)]" />
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--legatum-text-muted)]">
              Próximas {upcoming.length === 1 ? "tarea" : "tareas"}
            </div>
          </div>

          {upcoming.length === 0 ? (
            <div className="py-6 text-center text-xs text-[var(--legatum-text-muted)]" data-testid="calendar-widget-empty">
              No tienes tareas pendientes en los próximos 60 días.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="calendar-widget-upcoming">
              {upcoming.map((t) => {
                const fecha = t.fecha_fin || t.fecha_inicio;
                const accent = PRIORITY_COLOR[t.prioridad] || PRIORITY_COLOR.normal;
                return (
                  <li key={t.id}>
                    <Link
                      to={`/calendario?fecha=${fecha}`}
                      data-testid={`calendar-widget-task-${t.id}`}
                      className="flex items-start gap-2 px-2 py-2 rounded-lg hover:bg-[var(--legatum-elevated)] transition-colors"
                    >
                      <span aria-hidden className="self-stretch w-[3px] rounded-full mt-0.5" style={{ background: accent }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-[var(--legatum-text)] truncate flex items-center gap-1.5">
                          {(t.prioridad === "urgente" || t.prioridad === "alta") && (
                            <Flag className="h-3 w-3 shrink-0" style={{ color: accent }} />
                          )}
                          {t.titulo}
                        </div>
                        <div className="mt-0.5 text-[10px] text-[var(--legatum-text-muted)] truncate">
                          {fecha} {t.obra_codigo ? `· ${t.obra_codigo}` : ""}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </motion.section>
  );
}
